import { flow, join, map } from 'lodash/fp'

import {
  CATEGORIES_SEPARATOR,
  getOrganizationHeadquarters,
  getOrganizationOperatingLocations,
} from '../organizations/helpers'
import { Location, OrganizationLocation } from '../organizations/types'
import { Country } from './Country'

export const displayLocation = ({ city, region, countryCode, fullAddress }: Location): string => {
  if (!city && !region && !countryCode) {
    return fullAddress
  }
  const country = new Country(countryCode).name
  if (city || region) {
    return `${city || region} (${country})`
  }
  return country || '–'
}

const displayLocationRelation = ({ location }: OrganizationLocation) => location && displayLocation(location)

export const displayOrganizationHeadquarters = flow(
  getOrganizationHeadquarters,
  map(displayLocationRelation),
  join(CATEGORIES_SEPARATOR)
)

export const displayOrganizationOperatingLocations = flow(
  getOrganizationOperatingLocations,
  map(displayLocationRelation),
  join(CATEGORIES_SEPARATOR)
)

// Map of continent codes to continent names
const continentCodeMap: { [key: string]: string } = {
  EU: 'Europe',
  NA: 'North America',
  SA: 'South America',
  LA: 'Latin America',
  AS: 'Asia',
  ME: 'Middle East',
  AF: 'Africa',
  OC: 'Oceania',
  AN: 'Antarctica',
}

export const getAlpha3CodeFromLocation = (location: {
  countryCode?: string
  continent?: string
}): string[] | undefined => {
  const { countryCode, continent } = location

  if (countryCode) {
    // Check if the countryCode is actually a continent code
    const continentName = continentCodeMap[countryCode]
    if (continentName) {
      return getAlpha3CodeFromContinent(continentName)
    }

    const alpha3Code = new Country(countryCode).alpha3Code
    return alpha3Code ? [alpha3Code] : []
  }
  if (continent) {
    return getAlpha3CodeFromContinent(continent)
  }
  return undefined
}

export enum Continent {
  Africa = 'Africa',
  Antarctica = 'Antarctica',
  Asia = 'Asia',
  Europe = 'Europe',
  'Latin America' = 'Latin America',
  'Middle East' = 'Middle East',
  'North America' = 'North America',
  Oceania = 'Oceania',
  'South America' = 'South America',
}

const locationsMap: {
  [key in Continent]: string
} = {
  Europe:
    'ALA, ALB, AND, AUT, BLR, BEL, BIH, BGR, HRV, CZE, CYP, DNK, EST, FRO, FIN, FRA, DEU, GIB, GRC, GGY, VAT, HUN, ISL, IRL, IMN, ITA, JEY, KAZ, LVA, LIE, LTU, LUX, MLT, MDA, MCO, MNE, NLD, MKD, NOR, POL, PRT, ROU, RUS, SMR, SRB, SVK, SVN, ESP, SJM, SWE, CHE, TUR, UKR, GBR',
  ['North America']:
    'ATG, BHS, BRB, BMU, BLZ, VGB, CAN, CYM, CRI, CUB, DMA, DOM, SLV, GRL, GRD, GLP, GTM, HTI, HND, JAM, MTQ, MEX, MSR, ANT, CUW, ABW, SXM, BES, NIC, UMI, PAN, PRI, BLM, KNA, AIA, LCA, MAF, SPM, VCT, TTO, TCA, USA, VIR',
  ['South America']: 'ARG, BOL, BRA, CHL, COL, ECU, FLK, GUF, GUY, PER, PRY, SUR, URY, VEN',
  ['Latin America']:
    'AIA, ATG, ARG, ABW, BHS, BRB, BLZ, BOL, BES, BVT, BRA, CYM, CHL, COL, CRI, CUB, CUW, DMA, DOM, ECU, SLV, FLK, GUF, GRD, GLP, GTM, GUY, HTI, HND, JAM, MTQ, MEX, MSR, NIC, PAN, PRY, PER, PRI, BLM, KNA, LCA, MAF, VCT, SXM, SGS, SUR, TTO, TCA, URY, VEN, VGB, VIR',
  Asia:
    'AFG, ARE, ARM, AZE, BGD, BHR, BRN, BTN, CHN, CYP, GEO, IND, IDN, IRN, IRQ, ISR, JPN, JOR, KAZ, KHM, KOR, KWT, KGZ, LAO, LBN, MYS, MDV, MNG, MMR, NPL, OMN, PAK, PRK, PSE, PHL, QAT, RUS, SAU, SGP, LKA, SYR, TWN, TJK, THA, TLS, TUR, TKM, UZB, VNM, YEM',
  ['Middle East']: 'ARE, BHR, EGY, IRN, IRQ, ISR, JOR, KWT, LBN, LBY, OMN, PSE, QAT, SAU, SYR, TUR, YEM',
  Africa:
    'AGO, BDI, BEN, BFA, BWA, CAF, CIV, CMR, COD, COG, COM, CPV, DJI, DZA, EGY, ERI, ESH, ETH, GAB, GHA, GIN, GMB, GNB, GNQ, KEN, LBR, LBY, LSO, MAR, MDG, MLI, MOZ, MRT, MUS, MWI, MYT, NAM, NER, NGA, REU, RWA, SDN, SEN, SHN, SLE, SOM, SSD, STP, SWZ, SYC, TCD, TGO, TUN, TZA, UGA, ZAF, ZMB, ZWE',
  Oceania:
    'ASM, AUS, COK, FJI, PYF, KIR, MHL, FSM, NRU, NCL, NZL, NIU, NFK, MNP, PLW, PNG, PCN, WSM, SLB, TKL, TON, TUV, VUT, WLF',
  Antarctica: 'ATA',
}

export const getAlpha3CodeFromContinent = (continent: string): string[] => {
  const locations = locationsMap[continent] ?? null

  return locations ? locations.split(', ') : []
}
